import type { GA4PageNameType } from '@/ga4types'
import analytics from '@/shared-utils/analytics'
import { type FlowIdentifier } from '@okta/okta-auth-js'
import { type Socials } from './utils/types'

function flowToPageName(flow: FlowIdentifier) {
  const flowToPageMapping: { [key: string]: GA4PageNameType } = {
    authenticate: 'sign_in',
    register: 'account_creation',
  }
  return flowToPageMapping[flow]
}

const determineProduct = (redirectUrl: string) => {
  if (redirectUrl.includes('hotels')) return 'hotel'
  if (redirectUrl.includes('flights')) return 'air'
  if (redirectUrl.includes('cars')) return 'car'
  if (redirectUrl.includes('vacation')) return 'bundle'
  if (redirectUrl.includes('cruises')) return 'cruise'
  return 'hotel'
}

export function firePageViewEvent(activeFlow: FlowIdentifier) {
  const pageName = flowToPageName(activeFlow)
  /** Don't fire when no such mapping exists */
  if (pageName) {
    analytics.fireGA4Event({
      event: 'page_view',
      attributes: {
        page_name: pageName,
      },
    })
  }
}

export function fireLoginSuccessEvent(signInMethod: string, redirectUrl: string, activeFlow: FlowIdentifier) {
  const flowStatus = flowToPageName(activeFlow)
  const product = determineProduct(redirectUrl)
  analytics.fireGA4Event({
    event: 'login',
    attributes: {
      type: flowStatus === 'account_creation' ? 'sign_up' : 'sign_in',
      method: signInMethod,
      product_name: product,
      page_name: 'homepage',
      success_flag: 'y',
    },
  })
}

export function fireSignInClickEvent() {
  analytics.fireGA4Event({
    event: 'internal_element',
    attributes: {
      type: 'sign_in',
      link_name: 'sign_in',
      page_name: 'sign_in',
    },
  })
}

export function fireSocialSignInClickEvent(signInMethod: Socials) {
  analytics.fireGA4Event({
    event: 'internal_link',
    attributes: {
      type: 'sign_in',
      link_name: 'sign_in_with_social',
      link_item: signInMethod.toLowerCase(),
      page_name: 'sign_in',
    },
  })
}

export function fireCreateAccountEvent() {
  analytics.fireGA4Event({
    event: 'internal_element',
    attributes: {
      type: 'sign_up',
      link_name: 'create_account',
      page_name: 'account_creation',
    },
  })
}

export function fireSocialCreateAccountEvent(signUpMethod: Socials) {
  analytics.fireGA4Event({
    event: 'internal_link',
    attributes: {
      type: 'sign_up',
      link_name: 'sign_up_with_social',
      link_item: signUpMethod.toLowerCase(),
      page_name: 'account_creation',
    },
  })
}

export function fireEnterEmailCodeSignInEvent(isVerified: boolean) {
  analytics.fireGA4Event({
    event: 'api_call',
    attributes: {
      type: 'code_verification',
      page_name: 'sign_in',
      item_name: isVerified ? 'sign_in_verified' : 'sign_in_unverified',
    },
  })
}

export function fireEnterEmailCodeCreateAccountEvent() {
  analytics.fireGA4Event({
    event: 'api_call',
    attributes: {
      type: 'code_verification',
      page_name: 'account_creation',
      item_name: 'sign_up',
    },
  })
}

export function fireFindMyTripRedirectEvent() {
  analytics.fireGA4Event({
    event: 'internal_link',
    attributes: {
      type: 'find_trip',
      link_name: 'otp_verify',
      page_name: 'sign_in',
    },
  })
}

export function fireResendOtpSuccessEvent(activeFlow: FlowIdentifier | undefined) {
  if (activeFlow === undefined) {
    return
  }
  const pageName = flowToPageName(activeFlow)
  if (!pageName) {
    return
  }
  const type = pageName === 'account_creation' ? 'sign_up' : 'sign_in'
  analytics.fireGA4Event({
    event: 'internal_link',
    attributes: {
      type,
      link_name: 'resend_code',
      link_text: 'send_a_new_code',
      page_name: pageName,
    },
  })
}
